import React, { useEffect, useMemo, useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { AccountTree, List } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Theme } from '../../theme';
import { useIdentity } from '../../hooks/useIdentity';
import { useBillingNavigation } from '../../hooks/useBillingNavigation';
import ReactDOM from 'react-dom';
import { EntityPickerTree } from '../../EntityPickerTree';
import { EntityPickerList } from '../../EntityPickerList';

/**
 * Presents either:
 * - Universal identity (if enabled)
 * - Multientity
 **/
export const EntityPicker = ({ menuElement, menuType }) => {
  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);
  const { data: navigation } = useBillingNavigation();
  const { data } = useIdentity();

  const ZuoraCurrentEntity = data?.entityId;

  const entities =
    navigation && 'entities' in navigation
      ? (navigation.entities || []).sort((a, b) => a.name.localeCompare(b.name))
      : [];

  const [entityQuery, setEntityQuery] = useState('');
  // Entities are displayed as trees or list.
  const [showEntitiesAsTree, setShowEntitiesAsTree] = useState(true);
  // Switch to list view when querying entities.
  useEffect(() => {
    if (entityQuery) {
      setShowEntitiesAsTree(false);
    }
  }, [entityQuery]);
  // Clear query when switching to tree view.
  useEffect(() => {
    if (showEntitiesAsTree && entityQuery) {
      setEntityQuery('');
    }
  }, [showEntitiesAsTree]);

  const { t } = useTranslation();
  const currEntity = entities.find(
    (entity) =>
      entity?.id.replace(/-/g, '') ===
      `${ZuoraCurrentEntity || ''}`.replace(/-/g, ''),
  );
  const buttonLabel = useMemo(
    () => currEntity?.displayName || t('GlobalNav.MultiEntity.SelectEntity'),
    [currEntity, t],
  );

  useEffect(() => {
    // Create a container for our React content
    const container = document.createElement('div');
    container.slot = 'options';
    container.className = 'multi-entity-content-container';

    menuElement.appendChild(container);

    setContentContainer(container);

    return () => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, [menuElement]);

  useEffect(() => {
    if (!menuElement) return;

    const targetElement = menuType === 'entity' ? menuElement : menuElement;

    if (targetElement) {
      targetElement.setAttribute('label', buttonLabel);
    }
  }, [menuElement, buttonLabel]);

  if (!contentContainer) {
    return null;
  }

  const noEntities = !Boolean(entities?.length);
  if (noEntities) {
    return null;
  }

  const entityListResults = entities.filter(
    ({ name }) => name.toLowerCase().indexOf(entityQuery.toLowerCase()) > -1,
  );

  return (
    <>
      {ReactDOM.createPortal(
        <div className="multi-entity-react-content">
          {!((entities || []).length > 0) ? null : (
            <Theme>
              <div style={{ margin: '4px 0px 4px 8px' }}>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder={t('MenuSearchEntity')}
                  value={entityQuery}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEntityQuery(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t('GlobalNav.MultiEntity.ToggleView')}
                          onClick={() => {
                            setShowEntitiesAsTree(!showEntitiesAsTree);
                          }}
                        >
                          {showEntitiesAsTree ? <List /> : <AccountTree />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              {showEntitiesAsTree ? (
                <EntityPickerTree entities={entities} />
              ) : (
                <EntityPickerList entities={entityListResults} />
              )}
            </Theme>
          )}
        </div>,
        contentContainer,
      )}
    </>
  );
};

export default EntityPicker;
